import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { useQuery } from '@apollo/client';
import { queryCache } from 'react-query';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import { PageCard, PageContainer, Poppins, Spacer, TableSkeleton } from 'src/common';
import { Filter } from 'src/assets/dynamicSvg';
import GenericTable, { genEmtyRowTextComponent, GTRowEmpty } from 'src/components/GenericTable';
import { TableIds } from 'src/api/types/misc';
import { QKeys } from 'src/api/types';
import { isQueryLoading } from 'src/graphql';
import { ConfirmDeleteModal } from 'src/components/modals';
import { MY_EXPORT_JOBS } from 'src/graphql/schemas/queries/exportJob';
import { useDeleteUserExportJobsMutation } from 'src/graphql/_generated';
import { showSuccessToast, showErrorToast, toastMessages } from 'src/components/Toast';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import type { ExportReportTypeAnalytics } from 'src/utils/mixpanel/types';
import { MyExportJobsData, toReportRow } from './util';
import { useColumns } from './useColumns';
import { AuditSection } from './sections';

const Div = styled(PageContainer)`
  .panel {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    grid-gap: 10px;

    &__btns {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      grid-gap: 10px;
      flex-wrap: wrap;
    }
  }

  .filter-btn {
    height: 52px;
    width: 52px;
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;

    &:hover {
      filter: brightness(1.015);
    }

    ${({ theme }) => theme.breakpoints.down('md')} {
      height: 35px;
      width: 35px;
    }
  }
`;

// http://localhost:3333/report-centre/download/ccKxoihm

const ReportCentrePage = () => {
  useGaEventPageView('Cydea | Report Centre');

  const { data, loading, networkStatus, previousData } = useQuery<MyExportJobsData>(MY_EXPORT_JOBS, {
    notifyOnNetworkStatusChange: true,
    pollInterval: 1 * 60 * 1000,
  });

  const rows = _.orderBy(((data || previousData)?.myExportJobs || []).map(toReportRow), ['createdAt'], ['desc']);

  const isInitialLoading = isQueryLoading({ loading, data, networkStatus }) && !previousData;

  const { columns, GTColumnSelectAnchorExported, deleteModal, setDeleteModal } = useColumns({
    data: rows,
  });

  const [deleteExportJobs, { loading: deleting }] = useDeleteUserExportJobsMutation({
    update: (cache, { data }, { variables }) => {
      if (!data?.deleteUserExportJobs || !variables?.jobIds) return;
      const ids = Array.isArray(variables.jobIds) ? variables.jobIds : [variables.jobIds];
      ids.forEach((id: string) => {
        const cacheId = cache.identify({ __typename: 'ExportJob', id });
        if (cacheId) cache.evict({ id: cacheId });
      });
      cache.gc();
    },
    onCompleted: () => {
      // BE writes a User_Audit row for each delete — invalidate so the AUDIT
      // section refetches and the new "deleted" entry shows up immediately.
      queryCache.invalidateQueries([QKeys.Audit]);
      showSuccessToast(toastMessages.export.deleted);
      setDeleteModal({ id: null });
    },
    onError: (err) => {
      const serverMsg = err.graphQLErrors?.[0]?.message;
      showErrorToast(serverMsg || toastMessages.export.deleteFailed.title);
    },
  });

  return (
    <>
      <Meta title="Cydea | Report Centre" feedbackScript />
      {deleteModal.id && (
        <ConfirmDeleteModal
          onClose={() => setDeleteModal({ id: null })}
          onConfirm={async () => {
            if (!deleteModal.id || deleting) return;
            const jobId = deleteModal.id;
            const reportType = deleteModal.reportType;
            const result = await deleteExportJobs({ variables: { jobIds: [jobId] } });
            if (!result.errors) {
              mpEvent(MPEvents.ExportDelete, {
                reportType: (reportType || '').toUpperCase() as ExportReportTypeAnalytics,
                jobId,
              });
            }
          }}
        />
      )}
      <div className="page-content unstyled-scroll">
        <Div>
          <div className="panel">
            <Poppins px={32} weight={600}>
              Report Centre
            </Poppins>
            {/* <div className="panel__btns">
              <div className="filter-btn" data-cy="filters-control">
                <Filter />
              </div>
            </div> */}
          </div>
          <Spacer $px={20} />
          {!isInitialLoading ? (
            <>
              <GenericTable
                tableId={TableIds.reportCentre}
                data={rows}
                columns={columns}
                GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                searchable
                emptyRow={
                  <GTRowEmpty
                    text={genEmtyRowTextComponent({
                      startText: 'No activity found.',
                      endText:
                        'To generate a report, please generate a report from one of the following views: Insights, Assessment, Scenario, or Risk Management Plan.',
                    })}
                  />
                }
              />
              <Spacer $px={30} />
              <PageCard style={{ paddingTop: 0, paddingBottom: 0 }}>
                <AuditSection />
              </PageCard>
            </>
          ) : (
            <TableSkeleton />
          )}
        </Div>
      </div>
    </>
  );
};

export default ReportCentrePage;
